import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Upload, Loader2 } from "lucide-react";
import { compressImage, fileToDataUrl } from "@/lib/imageUtils";

export function CameraCapture({ onChange }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [camOn, setCamOn] = useState(false);
  const [starting, setStarting] = useState(false);
  const [shot, setShot] = useState(null);
  const [err, setErr] = useState(null);

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  };

  useEffect(() => {
    if (camOn && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [camOn]);

  useEffect(() => stopCam, []);

  const start = async () => {
    setErr(null);
    setStarting(true);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = s;
      setCamOn(true);
    } catch {
      setErr("カメラにアクセスできません。ブラウザのカメラ許可をご確認いただくか、画像ファイルを選択してください。");
    } finally {
      setStarting(false);
    }
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v) return;
    const vw = v.videoWidth || 640;
    const vh = v.videoHeight || 480;
    // ガイド枠（高さ92%・正方形・中央）の内側だけを切り出す
    const fh = 0.92 * vh;
    const fw = fh;
    const fx = (vw - fw) / 2;
    const fy = Math.max(0, (vh - fh) / 2);
    const c = document.createElement("canvas");
    c.width = Math.round(fw);
    c.height = Math.round(fh);
    c.getContext("2d").drawImage(v, fx, fy, fw, fh, 0, 0, c.width, c.height);
    const url = await compressImage(c.toDataURL("image/jpeg", 0.9));
    setShot(url);
    onChange(url);
    stopCam();
  };

  const retake = () => {
    setShot(null);
    onChange(null);
    start();
  };

  const fromFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = await compressImage(await fileToDataUrl(f));
    setShot(url);
    onChange(url);
    e.target.value = "";
  };

  return (
    <div>
      <span className="block text-xs text-white/50 mb-1.5">担当者の写真（本人確認のため撮影してください）</span>
      {shot ? (
        <div className="flex items-end gap-4">
          <img src={shot} alt="撮影写真" className="w-48 rounded-lg border border-white/15" data-testid="photo-preview" />
          <button
            type="button"
            onClick={retake}
            data-testid="photo-retake"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-xs text-white/70 hover:text-gold hover:border-gold/40 transition-colors"
          >
            <RefreshCw size={13} /> 撮り直す
          </button>
        </div>
      ) : camOn ? (
        <div className="space-y-3">
          <div className="relative w-72 max-w-full">
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg border border-white/15 bg-black" data-testid="camera-video" />
            {/* 顔写真ガイド枠（頭部シルエット・丸角正方形） */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative h-[92%]" style={{ aspectRatio: "1", boxShadow: "0 0 0 9999px rgba(0,0,0,0.4)" }}>
                <svg viewBox="0 0 300 300" className="absolute inset-0 h-full w-full" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" data-testid="camera-frame">
                  <rect x="12" y="12" width="276" height="276" rx="22" />
                  <path d="M150 30 C196 30 218 69 217 116 C216 151 210 173 200 189 C195 198 192 203 190 209 C188 217 190 225 193 233 C198 246 212 254 229 262 C256 274 272 281 278 290 L22 290 C28 281 44 274 71 262 C88 254 102 246 107 233 C110 225 112 217 110 209 C108 203 105 198 100 189 C90 173 84 151 83 116 C82 69 104 30 150 30 Z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={capture}
              data-testid="camera-capture"
              className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors"
            >
              <Camera size={15} /> 撮影する
            </button>
            <button
              type="button"
              onClick={stopCam}
              className="rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              キャンセル
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={start}
            disabled={starting}
            data-testid="camera-start"
            className="inline-flex items-center gap-2 rounded-lg border border-gold/40 text-gold px-4 py-2 text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
          >
            {starting ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} カメラで撮影
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="photo-upload"
            className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <Upload size={15} /> 画像ファイルを選択
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={fromFile} />
        </div>
      )}
      {err && <p className="mt-2 text-xs text-red-300" data-testid="camera-error">{err}</p>}
    </div>
  );
}
